<template>
  <div class="mb-5">
    <div v-if="isLoading" class="mt-5 d-flex justify-content-center">
      <b-spinner label="Loading..." variant="dark" />
    </div>
    <div v-else>
      <base-bread-crumb :items="breadcrumbs" />

      <vue-element-loading
        :active="isLoading"
        spinner="spinner"
        color="#FF6700"
        :is-full-screen="false"
        :background-color="'rgba(255, 255, 255, .3)'"
      />

      <div
        v-if="learningUnit"
        ref="learning-unit-name"
        class="h5 display-4 mt-1 mb-5 font-weight-bold"
      >
        {{ learningUnit.name }}
      </div>
      <div ref="action-title" class="h5 font-weight-bold mb-4">
        {{ postId ? 'Edit post' : 'Create post' }}
      </div>

      <!-- title -->
      <div ref="input-title">
        <label for="input-title"> Title </label>
        <b-form-input
          id="input-title"
          v-model.trim="$v.form.title.$model"
          type="text"
          placeholder="Insert your title"
          :disabled="isPostSchedulePublished || isSubmitting"
          :state="$v.form.title.$anyError ? false : null"
          aria-describedby="input-title-invalid-feedback"
          class="mt-2"
        />
        <b-form-invalid-feedback id="input-title-invalid-feedback" class="mt-2">
          {{ titleErrorMessage }}
        </b-form-invalid-feedback>
      </div>

      <div class="mt-4 mb-4">
        <b-form-group>
          <label id="quill-editor-label" class="mb-2"> Instructions </label>
          <div
            :class="[
              !$v.form.description.$model && $v.form.description.$anyDirty
                ? 'border border-danger'
                : ''
            ]"
          >
            <base-text-editor
              id="input-post-description"
              ref="myTextEditor"
              v-model="$v.form.description.$model"
              :disabled="isPostSchedulePublished || isSubmitting"
              :show-toolbar="!isPostSchedulePublished"
              placeholder="Insert your assessment instructions"
            ></base-text-editor>
          </div>
          <div
            v-if="!$v.form.description.$model && $v.form.description.$anyDirty"
            class="small text-error mt-1"
          >
            Instructions must be at least 1 character
          </div>
        </b-form-group>
      </div>
      <div class="mt-4 p-3 box-media">
        <label class="mb-2 h5"> Upload media </label><span class="text-muted"> (Optional)</span>
        <br />
        <label class="text-black-50">Additional resources for learner</label>

        <b-tabs
          v-model="$activeTab"
          class="mt-4"
          lazy
          pills
          nav-class="nav-line nav-line-alt"
          content-class="pt-4"
        >
          <b-tab>
            <template slot="title">
              <span ref="title__video" class="font-size-md active span__post-video-tab mr-2"
                >Video (1 file only)</span
              >

              <b-badge
                v-if="file.video && file.video.guid"
                ref="badge__video"
                variant="neutral-first"
                class="text-first text__post-video"
                >1</b-badge
              >

              <div class="divider" />
            </template>
          </b-tab>

          <b-tab>
            <template slot="title">
              <span ref="title__image" class="font-size-md active span__post-image-tab mr-2"
                >Image (Allow multiple images)</span
              >

              <b-badge
                v-if="file.images && file.images.length > 0"
                ref="badge__image"
                variant="neutral-first"
                class="text-first text__post-image"
                >{{ file.images.length }}</b-badge
              >

              <div class="divider" />
            </template>
          </b-tab>

          <b-tab>
            <template slot="title">
              <span
                ref="title__attachment"
                class="font-size-md active span__post-attachment-tab mr-2"
                >Attachment</span
              >

              <b-badge
                v-if="file.attachments && file.attachments.length > 0"
                ref="badge__attachment"
                variant="neutral-first"
                class="text-first text__post-attachment"
                >{{ file.attachments.length }}</b-badge
              >

              <div class="divider" />
            </template>
          </b-tab>
        </b-tabs>

        <div
          v-show="activeTab == 'VIDEO'"
          class="video-upload-wrapper dropzone-video mb-4 container__uploader"
          :class="{
            'is-processing': isProcessing,
            'is-disabled': isPostSchedulePublished || isSubmitting
          }"
        >
          <vue-dropzone
            v-if="!file.video || !file.video.guid"
            id="input-media"
            ref="videoDropzone"
            :options="dropzoneVideoOptions"
            @vdropzone-success="onSuccess"
            @vdropzone-file-added="handleUploadVideo"
            @vdropzone-removed-file="removeVideo"
          />

          <div v-if="file.video && file.video.guid">
            <div
              class="icon_remove-video d-flex justify-content-end"
              :class="{
                'is-disabled': isPostSchedulePublished || isSubmitting
              }"
            >
              <div>
                <font-awesome-icon
                  size="lg"
                  class="mb-2"
                  icon="times"
                  :style="{ color: 'red', cursor: 'pointer' }"
                  @click="removeVideo"
                />
              </div>
            </div>
            <base-video-bunny-player class="w-100" :bunny-video="file.video" />
          </div>
        </div>

        <div v-show="activeTab == 'IMAGE'" class="image-upload-wrapper mb-4">
          <base-s3-uploader
            id="input-media"
            ref="imageUploader"
            v-model="file.images"
            :reference-meta="{ ...referenceMeta, field: 'images' }"
            :dropzone-options="dropzoneImageOptionsV2"
            :disabled="isPostSchedulePublished || isSubmitting"
            :is-public="false"
          >
          </base-s3-uploader>

          <!-- <vue-dropzone
            id="input-media"
            ref="imageDropzone"
            :options="dropzoneImageOptions"
            @vdropzone-files-added="validateDropzoneImageWithDelay"
            @vdropzone-removed-file="removeImage"
            @vdropzone-total-upload-progress="imageUploadProcessing"
          />
          <small
            v-if="errMediaMsg"
            style="display: inline; color: red; margin-top: 0"
          >
            {{ errMediaMsg }}
          </small> -->
        </div>

        <div v-show="activeTab == 'ATTACHMENTS'" class="attactment-upload-wrapper mb-4">
          <div class="dropzone-attactment">
            <base-s3-uploader
              id="input-other-attachments"
              ref="attachmentUploader"
              v-model="file.attachments"
              :reference-meta="{ ...referenceMeta, field: 'attachments' }"
              :disabled="isPostSchedulePublished || isSubmitting"
              :is-public="false"
            >
            </base-s3-uploader>
          </div>
        </div>

        <!-- <div
          v-for="type in uploadMediaType"
          :id="`select-media-type-${type.id}`"
          :key="type.id"
        >
          <b-form-radio
            v-model="form.mediaType"
            class="my-1 mb-2"
            :disabled="isSubmitting"
            :value="type.id"
          >
            <div>
              {{ type.title }}
              <span class="text-dark">{{ type.option }}</span>
              <span
                v-if="
                  file.oldVideo &&
                  file.oldVideo.video_link &&
                  !file.video_link_vimeo &&
                  type.title === 'Video'
                "
                class="ml-2"
                ><a
                  href="javascript:void(0);"
                  :style="{ cursor: 'pointer' }"
                  @click="reverseDeletedVideo"
                  >Reverse</a
                >
              </span>
            </div>
          </b-form-radio>
        </div> -->
      </div>

      <div class="mt-4">
        <BaseThumbnailUpload
          ref="thumbnailUploader"
          :reference-meta="referenceMeta"
          :file.sync="file.thumbnail"
          :disabled="isPostSchedulePublished || isSubmitting"
        ></BaseThumbnailUpload>
      </div>

      <div ref="published-date" class="mt-4">
        <div class="mb-3">Published date</div>

        <b-alert show variant="info"> Bangkok Time </b-alert>

        <BaseDatePicker
          :existed-data="form.published_at"
          :disabled="isPostSchedulePublished"
          date-picker-class="w-25 mr-2"
          @emitDate="setPublishDate"
        />

        <div
          v-if="!$v.form.published_at.$model && $v.form.published_at.$anyDirty"
          class="small text-error mt-2"
        >
          The published date is a required field.
        </div>
      </div>

      <div v-if="shouldDisplaySectionAddToYourPost" ref="add-to-your-post" class="mt-4">
        <div>
          <span class="h5 font-weight-bold"> Add to your post </span>
          <span class="text-muted"> (Optional) </span>
        </div>
        <label class="text-black-50">This post can assess with learning outcome</label>

        <b-alert :show="!disabledSubmissionSection" variant="info" class="my-3"
          >You cannot change this setting once the learning unit is published and there're learners
          send submission to this post.</b-alert
        >

        <b-col class="box-submission mb-5 p-3">
          <b-form-checkbox
            v-model="form.is_submission"
            name="check-button"
            switch
            :disabled="disabledSubmissionSection || isSubmitting || isPostSchedulePublished"
            class="custom-control__submission-toggle"
          >
            <div class="d-flex flex-column">
              <span class="h5"> Submission </span>
              <label class="text-black-50">This post learner can submit evidence</label>
            </div>
          </b-form-checkbox>

          <b-col v-show="form.is_submission" class="mt-4">
            <b-row align-v="center">
              <p>
                Evaluate with Learning Outcome
                <span class="text-muted">(Optional)</span>
              </p>
            </b-row>

            <b-row align-v="center">
              <b-button
                variant="outline-primary"
                class="mr-3 btn__add-existing-criteria"
                :disabled="disabledSubmissionSection || isSubmitting || isPostSchedulePublished"
                @click="openModalExistingCriteria()"
              >
                Add existing criterion
              </b-button>

              <span>
                <span class="text-dark mr-3">or</span>

                <b-button
                  variant="outline-primary"
                  class="btn__create-new-criteria"
                  :disabled="disabledSubmissionSection || isSubmitting || isPostSchedulePublished"
                  @click="openModalCreateNewCriteria"
                >
                  Create new criteria
                </b-button>
              </span>
            </b-row>

            <b-row v-for="(criterion, index) of formCriteria" :key="`criterion-${criterion.id}`">
              <b-col class="px-0 pt-4 card__criteria-list">
                <AdditionalCriterionList
                  :criterion="criterion"
                  :learning-outcome="learningOutcome"
                  :disabled="disabledSubmissionSection || isPostSchedulePublished"
                  :can-edit="!disabledSubmissionSection"
                  @remove="onCriterionRemoved(index)"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-col>

        <ModalAddExistingCriteria
          :show.sync="showModalExistingCriteria"
          :selected-criteria.sync="form.criteria"
          :learning-unit-id="learningUnitId"
          :learning-outcome="learningOutcome"
        ></ModalAddExistingCriteria>

        <ModalCreateNewCrtieria
          :show.sync="showModalCreateNewCrtieria"
          :selected-criteria.sync="form.criteria"
          :learning-outcome="learningOutcome"
          :learning-unit-id="learningUnitId"
          @onSelectCriterion="openModalExistingCriteria"
        ></ModalCreateNewCrtieria>
      </div>

      <div class="border-top pt-4">
        <b-button
          id="button-save"
          type="submit"
          class="mr-3 mb-5"
          variant="success"
          :disabled="isPostSchedulePublished || isSubmitting"
          @click="submit"
        >
          <span v-if="!isSubmitting">{{ postId ? 'Save' : 'Create' }}</span>

          <span v-else class="pr-2 pl-2">
            <b-spinner label="Loading..." variant="secondary" small />
          </span>
        </b-button>
        <b-button
          id="button-back-to-previous"
          type="reset"
          class="mb-5"
          variant="link btn-link-dark mr-3 h-50"
          :disabled="isSubmitting"
          @click="goBackToPreviousPage"
        >
          Back to previous page
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { Post, BunnyVideo, LearningOutcome, LearningUnit } from '@/models';

import { notificationMixin } from '@/mixins/notificationMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';

import { required, minLength, maxLength } from 'vuelidate/lib/validators';

import vue2Dropzone from 'vue2-dropzone';
import * as tus from 'tus-js-client';

import AdditionalCriterionList from './AdditionalCriterionList';
import BaseBreadCrumb from '@/components/base-components/BaseBreadCrumb';
import BaseVideoBunnyPlayer from '@/components/base-components/BaseVideoBunnyPlayer';
import BaseS3Uploader from '@/components/base-components/BaseS3Uploader';
// import AdditionalCriterion from "@/models/AdditionalCriterion";
import BaseDatePicker from '@/components/base-components/BaseDatePicker';
import PostSchedule from '@/models/PostSchedule';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ModalAddExistingCriteria from './ModalAddExistingCriteria';
import ModalCreateNewCrtieria from './ModalCreateNewCrtieria';
import Criteria from '@/models/Criteria';
import BaseThumbnailUpload from '@/components/base-components/BaseThumbnailUpload';
import BaseTextEditor from '@/components/base-components/BaseTextEditor';

library.add(faSearch, faTimes);

const nameTabs = ['VIDEO', 'IMAGE', 'ATTACHMENTS'];

export default {
  name: 'CreateOrEditPost',

  components: {
    vueDropzone: vue2Dropzone,
    AdditionalCriterionList,
    BaseVideoBunnyPlayer,
    BaseS3Uploader,
    'font-awesome-icon': FontAwesomeIcon,
    BaseBreadCrumb,
    BaseDatePicker,
    ModalAddExistingCriteria,
    ModalCreateNewCrtieria,
    BaseThumbnailUpload,
    BaseTextEditor
  },

  mixins: [notificationMixin, userInfoMixin],

  data() {
    return {
      activeTab: 0,
      isLoading: false,
      isSubmitting: false,
      isShowModal: false,
      isProcessing: false,
      // formEditCriterion: {},
      // criterionIndex: null,
      // deletedAdditionalCriteriaIds: [],
      showModalExistingCriteria: false,
      showModalCreateNewCrtieria: false,
      isCreateLearningOutcome: false,
      form: {
        title: '',
        description: '',
        // evaluate_learning_outcome: false,
        // additional_criterion: [],
        learning_unit: null,
        learning_outcome: null,
        published_at: new Date(),
        is_submission: false,
        criteria: this.$route.query.criteria || []
      },

      referenceMeta: {
        ref: 'post',
        refId: null
      },

      file: {
        oldVideo: null,
        video: null,
        percentageUploadVideo: null,
        images: [],
        imagesIdDeleted: [], // list of image id that user want to delete
        // percentageUploadImage: null,
        attachments: [],
        attachmentsIdDeleted: [], // list of attachment id that user want to delete
        // percentageUploadAttach: null,
        thumbnail: [],
        thumbnailIdDeleted: [] // list of attachment id that user want to delete
      },

      errMediaMsg: '',
      dropzoneVideoOptions: {
        url: 'https://httpbin.org/post',
        autoProcessQueue: false,
        addRemoveLinks: true,
        maxFiles: 1,
        parallelUploads: 1,
        acceptedFiles: '.video,.mp4,.mov,.avi',
        error: (file, message) => {
          if (message === "You can't upload files of this type.") {
            this.$refs.videoDropzone.removeFile(file);
            this.notiIncorrectFileType();
          }
        }
      },

      dropzoneImageOptions: {
        url: 'https://httpbin.org/post',
        autoProcessQueue: false,
        addRemoveLinks: true,
        uploadMultiple: true,
        acceptedFiles: '.jpeg,.jpg,.png',
        error: (file, message) => {
          if (message === "You can't upload files of this type.") {
            this.$refs.imageDropzone.removeFile(file);
            this.notiIncorrectImageType();
          }
        },
        init: () => {
          this.$nextTick(() => {
            if (this.file.images.length) {
              this.file.images.forEach((image) => {
                const { id, size, name, ext, url } = image;
                const file = { size, name, type: ext, id };

                if (this.$refs.imageDropzone) {
                  this.$refs.imageDropzone.manuallyAddFile(file, url);
                }
              });
            }
          });
        }
      },

      dropzoneImageOptionsV2: {
        acceptedFiles: '.jpeg, .jpg, .png'
      },

      dropzoneAttachOptions: {
        url: 'https://httpbin.org/post',
        autoProcessQueue: false,
        addRemoveLinks: true,
        uploadMultiple: true,
        init: () => {
          this.$nextTick(() => {
            if (this.file.attachments.length) {
              this.file.attachments.forEach((attachment) => {
                const { id, size, name, ext, url } = attachment;
                const file = { size, name, type: ext, id };

                if (this.$refs.attachmentDropzone) {
                  this.$refs.attachmentDropzone.manuallyAddFile(file, url);
                }
              });
            }
          });
        }
        // maxFilesize: 0.5,
        // headers: {
        //   Authorization:
        //     "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MSwiaWF0IjoxNTk5MTA3NDA3LCJleHAiOjE2MDE2OTk0MDd9.nsgzABsxfYpiLj4uXh3PGb3udfUb_--rlpoGodiOrF0",
        // },
      },

      dropzoneThunnailOptions: {
        acceptedFiles: '.jpeg, .jpg, .png'
      }
    };
  },

  validations: {
    form: {
      title: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(255)
      },
      description: { required },
      published_at: { required }
    }
  },

  computed: {
    $activeTab: {
      set(index) {
        this.activeTab = nameTabs[index];
      },
      get() {
        const indexTab = nameTabs.indexOf(this.activeTab);

        return Number(indexTab);
      }
    },

    breadcrumbs() {
      const { learningUnitId } = this;
      const { view = 'feed' } = this.$route.query;

      return [
        {
          key: 'design-and-planning',
          name: 'Design and planning',
          to: {
            name: 'design-planning'
          }
        },
        {
          key: 'learning-unit-info',
          name: this.learningUnit?.name || '',
          to: {
            name: 'design-planning/info',
            params: {
              learningUnitId
            }
          }
        },
        {
          key: 'teaching-feed',
          name: 'Teaching feed',
          to: {
            name: 'design-planning/teaching-feed',
            params: {
              learningUnitId: this.learningUnitId,
              sectionId: this.sectionId
            },
            query: {
              view: view
            }
          }
        },
        {
          key: 'manage-post',
          name: (this.postId ? 'Edit' : 'Create') + ' post',
          active: true
        }
      ];
    },

    editorOption() {
      return {
        formats: ['align', 'link', 'indent', 'list'],
        modules: {
          toolbar: this.isPostSchedulePublished
            ? false
            : [
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ align: [] }],
                ['link']
              ]
        },
        placeholder: 'Insert your assessment instructions'
      };
    },

    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    postId() {
      return Number.parseInt(this.$route.params.postId);
    },

    learningUnit() {
      return LearningUnit.query().with('learning_outcome').find(this.learningUnitId);
    },

    learningOutcome() {
      return LearningOutcome.query().where('learning_unit_id', this.learningUnitId).first();
    },

    sectionId() {
      return Number.parseInt(this.$route.params.sectionId);
    },

    post() {
      return (
        Post.query()
          .where('id', this.postId)
          .with('criteria')
          // .with("additional_criterion")
          .with('learning_unit')
          .with('bunny_video')
          .first()
      );
    },

    postSchedule() {
      return PostSchedule.query()
        .where('section_id', this.sectionId)
        .where('post_id', this.postId)
        .first();
    },

    isPostSchedulePublished() {
      let isPostPublished = false;

      if (this.learningUnit.is_published) {
        if (this.postSchedule) {
          const currentDate = new Date();
          const postScheduleDate = new Date(this.postSchedule.published_at);

          isPostPublished = currentDate >= postScheduleDate;
        }
      }

      return isPostPublished;
    },

    titleErrorMessage() {
      const titleValidate = this.$v.form.title;
      if (!titleValidate.required) {
        return 'The Title is a required field.';
      } else {
        if (!titleValidate.minLength) {
          return 'The Title must be at least 3 characters.';
        } else if (!titleValidate.maxLength) {
          return ' The Title not be greater than 255 characters.';
        } else return '';
      }
    },

    isPageCreatePost() {
      return Number.isNaN(this.postId);
    },

    isPageEditPost() {
      return Boolean(this.postId);
    },

    shouldDisplaySectionAddToYourPost() {
      return (
        (this.isPageCreatePost && !this.learningUnit.is_published) ||
        (this.isPageEditPost && this.post.is_submission)
      );
    },

    formCriteria() {
      const { criteria = [] } = this.form;

      return criteria.map((id) => Criteria.find(id));
    },

    disabledSubmissionSection() {
      if (this.user.isRole('reviewer')) return false;

      if (this.learningUnit.is_published) return true;

      return false;
    }
  },

  watch: {
    '$route.path': 'fetch'
  },

  async created() {
    await this.fetch();
  },

  methods: {
    onSuccess(file, response) {
      console.log('on dropzone success', file, response);
    },
    async fetch() {
      let response;

      this.isLoading = true;

      try {
        // PostType.deleteAll();
        await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);

        // ({ response } = await PostType.api().fetchAll());

        if (this.$route.query.isSubmission) {
          this.$set(this.form, 'is_submission', true);
        }

        if (this.postId) {
          // edit post
          // learning unit data will get together with post
          ({ response } = await Post.api().fetchById(this.postId));

          // if (this.learningUnit.learning_outcome_id) {
          //   ({ response } = await LearningOutcome.api().fetchById(
          //     this.learningUnit.learning_outcome_id
          //   ));
          // }
        } else {
          // create post
          // ({
          //   response,
          // } = await LearningUnit.api().fetchByIdWithOnlyLearningOutcome(
          //   this.learningUnitId
          // ));
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }

      if (this.postId) {
        this.initDataForEditPost();
      }

      return response;
    },

    async initDataForEditPost() {
      // create page doesn't have postId
      if (this.postId) {
        this.isLoading = true;

        this.form = {
          title: this.post.title,
          description: this.post.description,
          // additional_criterion: this.post.additional_criterion,
          learning_unit: this.post.learning_unit_id,
          learning_outcome: this.post.learning_outcome_id,
          criteria: [...this.post.criterion_ids],
          is_submission: this.post.is_submission
        };

        this.$set(this.referenceMeta, 'refId', this.post.id);

        //fetch post schedule and set data form.published_at
        await this.fetchPostSchedule();

        if (this.post.bunny_video) {
          this.$set(this.file, 'oldVideo', this.post.bunny_video);
          this.$set(this.file, 'video', this.post.bunny_video);
        }

        this.$set(this.file, 'attachments', this.post.attachments);

        this.$set(this.file, 'images', this.post.images);

        this.$set(this.file, 'thumbnail', this.post.thumbnail);

        // this.initImageDropzone();
        // this.initAttachFileDropzone();

        this.isLoading = false;
      }
    },

    async fetchLearningOutcome() {
      try {
        if (this.learningUnit.ultimate_learning_outcome_id) {
          await LearningOutcome.api().fetchById(this.learningUnit.ultimate_learning_outcome_id);
        }
      } catch (error) {
        this.$toast.error('Cannot load criteria, please try again');
      }
    },

    async openModalExistingCriteria() {
      await this.fetchLearningOutcome();

      this.showModalExistingCriteria = true;
    },

    async openModalCreateNewCriteria() {
      await this.fetchLearningOutcome();

      this.showModalCreateNewCrtieria = true;
    },

    async fetchPostSchedule() {
      const postScheduleResponse = await PostSchedule.api().findAll({
        post: this.postId,
        section: this.sectionId,
        _populate: [
          'post.criteria',
          'post.attachments',
          'post.images',
          'post.bunny_video',
          'post.assessment_statuses.user',
          'post.meeting',
          'post.thumbnail'
        ]
      });

      const postScheduleResponseData = postScheduleResponse.response.data;

      if (postScheduleResponseData && postScheduleResponseData.length) {
        const postSchedule = postScheduleResponseData[0];

        this.form.published_at = postSchedule.published_at;

        await this.updateRequireReviewAndReviewAt(postSchedule.id);
      }
    },

    async updateRequireReviewAndReviewAt(postScheduleId) {
      const postSchedule = PostSchedule.find(postScheduleId);

      if (postSchedule && postSchedule.required_review) {
        await PostSchedule.api().markAsReviewed(postSchedule.id);
      }
    },

    initImageDropzone() {
      this.$nextTick(() => {
        if (this.file && this.file.images && this.file.images.length) {
          this.file.images.forEach((image) => {
            const { id, size, name, ext, url } = image;
            const file = { size, name, type: ext, id };
            if (this.$refs.imageDropzone) {
              this.$refs.imageDropzone.manuallyAddFile(file, url);
            }
          });
        }
      });
    },

    initAttachFileDropzone() {
      this.$nextTick(() => {
        if (this.file && this.file.attachments && this.file.attachments.length) {
          this.file.attachments.forEach((attachment) => {
            const { id, size, name, ext, url } = attachment;
            const file = { size, name, type: ext, id };

            if (this.$refs.attachmentDropzone) {
              this.$refs.attachmentDropzone.manuallyAddFile(file, url);
            }
          });
        }
      });
    },

    async submit() {
      this.$v.form.$touch();

      // check title
      if (this.$v.form.title.$invalid) {
        this.scrolltoView('input-title');
        return;
      }

      /* Validate description/instruction before post/edit  */
      if (this.$v.form.description.$anyError) {
        /* Scroll to title */
        this.scrolltoView('action-title');
        return;
      }

      if (this.$v.form.published_at.$anyError) {
        this.scrolltoView('published-date');
        return;
      }

      /* Post/Edit data to API */
      this.isSubmitting = true;

      this.$refs.videoDropzone ? this.$refs.videoDropzone.processQueue() : null;

      this.$refs.attachmentDropzone ? this.$refs.attachmentDropzone.processQueue() : null;

      let postResponse;

      if (this.form.is_submission === false) {
        this.form.criteria = [];
        this.$set(this.form, 'post_type', 'LEARNING');
      } else {
        this.$set(this.form, 'post_type', 'FORMATIVE_ASSESSMENT');
      }

      if (this.postId) {
        postResponse = await this.updatePost(this.postId, this.form);
      } else {
        this.$set(this.form, 'learning_unit', this.learningUnitId);

        postResponse = await this.createPost(this.form);
      }

      if (this.postId) {
        this.notiSuccess('post', 'updated');
      } else {
        this.notiSuccess('post', 'created');
      }

      this.isSubmitting = false;

      this.goToInfoPostsListPage();

      return postResponse;
    },

    async updatePostSchedule(post_id, section_id) {
      if (this.form.published_at) {
        await PostSchedule.api().findAll({
          post: post_id,
          section: section_id
        });

        const postSchedule = PostSchedule.query()
          .where(
            (postScedule) => postScedule.post_id == post_id && postScedule.section_id == section_id
          )
          .first();

        await PostSchedule.api().update(postSchedule.id, {
          published_at: this.form.published_at,
          required_review: false,
          reviewed_at: Date.now()
        });
      }
    },

    async createPost(body) {
      let res;

      try {
        res = await Post.api().createPost(body);

        const post_id = res.response.data.id;

        this.$set(this.referenceMeta, 'refId', post_id); // set the reference meta data for BaseS3Uploader

        await this.updatePostSchedule(post_id, this.sectionId);

        if (this.file.video) {
          await this.resumableUpload(post_id, this.file.video); // upload video
        }

        if (this.$refs.attachmentUploader.hasAnyQueuedFiles()) {
          await this.$refs.attachmentUploader.upload();
        }

        if (this.$refs.imageUploader.hasAnyQueuedFiles()) {
          await this.$refs.imageUploader.upload();
        }

        if (this.$refs.thumbnailUploader.$children[0].hasAnyQueuedFiles()) {
          await this.$refs.thumbnailUploader.$children[0].upload();
        }
      } catch (error) {
        console.log(error);
        this.$toast.error('Cannot create post, please try again');
      }

      return res;
    },

    async updatePost(id, body) {
      let res;

      try {
        // if remove previous video, set null to bunny_video
        if (!this.file.video) {
          this.$set(body, 'bunny_video', null);
        }

        res = await Post.api().updatePost(id, body);

        const post_id = res.response.data.id;
        const criteria = res.response.data.criteria;
        const bunny_video = res.response.data.bunny_video;

        // if remove video, delete video in bunny and delete record of video in bunny_video model too
        if (!bunny_video) {
          await BunnyVideo.delete((bunnyVideo) => bunnyVideo.post_id == post_id);
        }

        await Post.update({
          where: post_id,
          data: {
            criteria,
            criterion_ids: criteria.map(({ id }) => id)
          }
        });

        await this.updatePostSchedule(post_id, this.sectionId);

        if (this.file.video && !this.file.video.guid) {
          // trigger upload video when only new video was attached.
          await this.resumableUpload(post_id, this.file.video);
        }

        if (this.$refs.attachmentUploader.hasAnyDeletedFiles()) {
          await this.$refs.attachmentUploader.deleteRemovedFiles();
        }

        if (this.$refs.imageUploader.hasAnyDeletedFiles()) {
          await this.$refs.imageUploader.deleteRemovedFiles();
        }

        if (this.$refs.thumbnailUploader.$children[0].hasAnyDeletedFiles()) {
          await this.$refs.thumbnailUploader.$children[0].deleteRemovedFiles();
        }

        if (this.$refs.attachmentUploader.hasAnyQueuedFiles()) {
          await this.$refs.attachmentUploader.upload();
        }

        if (this.$refs.imageUploader.hasAnyQueuedFiles()) {
          await this.$refs.imageUploader.upload();
        }

        if (this.$refs.thumbnailUploader.$children[0].hasAnyQueuedFiles()) {
          await this.$refs.thumbnailUploader.$children[0].upload();
        }

        // if (this.file.imagesIdDeleted.length) {
        //   const { images = [] } = this.post;

        //   for (
        //     let index = 0;
        //     index < this.file.imagesIdDeleted.length;
        //     index++
        //   ) {
        //     const imageId = this.file.imagesIdDeleted[index];

        //     await Upload.api().deleteFile(imageId);

        //     const imageIndex = images.findIndex((image) => image.id == imageId);

        //     if (imageIndex !== -1) {
        //       images.splice(imageIndex, 1);
        //     }
        //   }

        //   Post.update({
        //     where: this.post.id,
        //     data: {
        //       images,
        //     },
        //   });
        // }

        // if (this.file.attachmentsIdDeleted.length) {
        //   const { attachments = [] } = this.post;

        //   for (
        //     let index = 0;
        //     index < this.file.attachmentsIdDeleted.length;
        //     index++
        //   ) {
        //     const attachmentId = this.file.attachmentsIdDeleted[index];

        //     await Upload.api().deleteFile(attachmentId);

        //     const attachmentIndex = attachments.findIndex(
        //       (attachment) => attachment.id == attachmentId
        //     );

        //     if (attachmentIndex !== -1) {
        //       attachments.splice(attachmentIndex, 1);
        //     }
        //   }

        //   Post.update({
        //     where: this.post.id,
        //     data: {
        //       attachments,
        //     },
        //   });
        // }
      } catch (error) {
        console.log(error);
        this.$toast.error('Cannot update post, please refresh and try again');
      }

      return res;
    },

    setPublishDate(date) {
      this.form.published_at = date;
    },

    deleteFiles() {},

    scrolltoView(selector) {
      this.$refs[selector].scrollIntoView({ behavior: 'smooth' });
    },

    onCriterionRemoved(index) {
      this.form.criteria.splice(index, 1);
    },

    async handleUploadVideo(file) {
      // Check if file type is video
      if (!file.type.includes('video')) {
        this.isProcessing = true;
        this.$refs.videoDropzone.removeAllFiles();
        this.notiIncorrectFileType();
      } else {
        this.file.video = file;
      }
    },
    removeVideo() {
      if (this.isSubmitting) return;

      if (!this.postId) {
        this.$set(this.file, 'video', null);
      }

      if (this.file.video && this.file.video.guid && this.postId) {
        this.$set(this.file, 'video', null);
      }
    },
    reverseDeletedVideo() {
      this.$set(this.file, 'video', this.file.oldVideo);
    },
    // handleUploadAttach(file) {
    //   this.$set(this.file, "attachments", [...this.file.attachments, ...file]);
    // },
    // removeAttach(file) {
    //   if (this.isSubmitting) return;

    //   // remove new selected file
    //   if (file.upload) {
    //     this.$set(
    //       this.file,
    //       "attachments",
    //       [...this.file.attachments].filter(
    //         (attachment) => attachment.upload.uuid !== file.upload.uuid
    //       )
    //     );
    //   }
    //   // remove previous uploaded file
    //   else {
    //     this.$set(
    //       this.file,
    //       "attachments",
    //       [...this.file.attachments].filter(
    //         (attachment) => attachment.id !== file.id
    //       )
    //     );

    //     this.file.attachmentsIdDeleted.push(file.id);
    //   }
    // },
    // eslint-disable-next-line no-unused-vars
    // attachUploadProcessing(totaluploadprogress, totalBytes, totalBytesSent) {
    //   if (this.isLoading) {
    //     this.percentageUploadAttach = totaluploadprogress;
    //   }
    // },

    // removeImage(file) {
    //   if (this.isSubmitting) return;
    //   this.validateDropzoneImage();
    //   // delete old file
    //   if (!file.upload) this.file.imagesIdDeleted.push(file.id);
    // },
    // eslint-disable-next-line no-unused-vars
    // imageUploadProcessing(totaluploadprogress, totalBytes, totalBytesSent) {
    //   if (this.isLoading) {
    //     this.percentageUploadImage = totaluploadprogress;
    //   }
    // },

    /**
     * Upload video to Bunny Stream with resumable upload using tus protocol
     * @param {Number,String} post_id
     * @param {File} video
     *
     * */
    async resumableUpload(post_id, video) {
      const body = {
        post_id,
        title: this.form.title
      };

      try {
        const { response } = await BunnyVideo.api().getUploadToken(body);

        if (!response?.data) {
          throw new Error('ERROR: Failed to get Bunny upload token');
        }

        const { guId, signature, expiration } = response.data;
        const libraryId = process.env.VUE_APP_BUNNY_STREAM_LIBRARY_ID;

        // Use tus-js-client to upload video to Bunny Stream with tus protocol
        const upload = new tus.Upload(video, {
          endpoint: 'https://video.bunnycdn.com/tusupload',
          retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000], // retry and max retry delay of 1 minute
          removeFingerprintOnSuccess: true,

          // generate unique fingerprint for each file to allow resumable upload and avoid conflict with other uploads
          fingerprint: async (file) => `bunny-${guId}-${file.name}-${file.size}`,

          metadata: {
            title: this.form.title || 'Untitled',
            filetype: video.type
          },
          headers: {
            AuthorizationSignature: signature,
            AuthorizationExpire: expiration.toString(),
            VideoId: guId,
            LibraryId: libraryId
          },

          // Retry when http status is 5xx or network error
          onShouldRetry: (error) => {
            const status = error.originalResponse?.getStatus();
            if (status === 401 || status === 403) return false;
            if (status >= 400 && status < 500) return false;
            return true;
          },

          onSuccess: () => {
            this.$set(this.file, 'video', {
              guid: guId,
              title: this.form.title
            });
          },

          onError: (error) => {
            if (error.originalRequest) {
              console.error('Server error:', error.message);
            } else {
              // Client-side error
              console.error('Upload error:', error.message);
            }
            this.isLoading = false;
            this.notiError();
          }
        });

        // Check for previous uploads to allow resuming if the upload was interrupted
        const previousUploads = await upload.findPreviousUploads();
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }

        upload.start(); // Start the upload
      } catch (error) {
        console.error('resumableUpload error:', error);
        this.isLoading = false;
        this.notiError();
      }
    },

    // /**
    //  * Upload file only for post collection
    //  *
    //  * @param {String} field field ex. image, attachments, type of files
    //  * @param {Number,String} post_id
    //  */
    // async uploadFiles(field, post_id) {
    //   let promise;

    //   const formData = new FormData();
    //   formData.append("ref", "post");
    //   formData.append("refId", post_id);
    //   formData.append("field", field);

    //   let fileCount = 0;
    //   this.file[field].forEach((file) => {
    //     if (!file.id) {
    //       formData.append("files", file);
    //       fileCount++;
    //     }
    //   });

    //   if (fileCount) {
    //     try {
    //       promise = await Upload.api().upload(formData);
    //     } catch (error) {
    //       console.error(`uploadFiles field: ${field} error: `, error);
    //     }
    //   }

    //   return promise;
    // },

    // async uploadFileFromDropZone(ref, field, post_id) {
    //   let promise;

    //   const formData = new FormData();
    //   formData.append("ref", "post");
    //   formData.append("refId", post_id);
    //   formData.append("field", field);

    //   let fileCount = 0;
    //   let files = this.$refs[ref].getAcceptedFiles();
    //   files.forEach((file) => {
    //     if (!file.id) {
    //       formData.append("files", file);
    //       fileCount++;
    //     }
    //   });

    //   if (fileCount) {
    //     try {
    //       promise = await Upload.api().upload(formData);
    //     } catch (error) {
    //       console.error(`uploadFiles field: ${field} error: `, error);
    //     }
    //   }

    //   return promise;
    // },

    // showModal(modalType, index) {
    //   if (index || index === 0) {
    //     // edit criterion
    //     // this.formEditCriterion = this.form.additional_criterion[index];
    //     // this.criterionIndex = index;
    //     this.$refs[modalType].show(modalType);
    //   } else {
    //     this.$refs[modalType].show(modalType);
    //   }
    // },

    // showModalDelete(index) {
    //   // this.criterionIndex = index;
    //   this.showModal(`modalDelete__${this.criterionIndex}`, index);
    // },

    // closeModal() {
    //   this.isShowModal = false;
    //   // this.formEditCriterion = {};
    // },

    // openModal() {
    //   this.isShowModal = true;
    // },

    goBackToPreviousPage() {
      this.$router.go(-1);
    },

    goToInfoPostsListPage() {
      const { previousPath = '', view = 'feed' } = this.$route.query;
      if (previousPath) {
        this.$router.push({ path: previousPath });
      } else {
        this.$router.push({
          name: 'design-planning/teaching-feed',
          params: {
            learningUnitId: this.learningUnitId,
            sectionId: this.sectionId
          },
          query: {
            view: view
          }
        });
      }
    },

    gotoCreateLearningOutcome() {
      this.$router.push({
        name: 'design-planning/learning-outcome/create',
        params: {
          learningUnitId: this.learningUnitId
        },
        query: {
          backToPrevious: true
        }
      });
    },

    onVideoReady(value) {
      this.$set(this.file.video, 'isVideoReady', value);
    },

    onVideoReload(updatedVideo) {
      this.$set(this.file, 'video', updatedVideo);
    }

    /* ต้อง delay ตัว validate เพราะว่ามันถูก trigger ก่อนที่ไฟล์จะถูกเซฟเข้าตัวแปร */
    // async validateDropzoneImageWithDelay() {
    //   setTimeout(() => {
    //     this.validateDropzoneImage();
    //   }, 500);
    // },

    // async validateDropzoneImage() {
    //   const dropzone = document.querySelector(
    //     ".image-upload-wrapper > .vue-dropzone > .dz-message"
    //   );
    //   if (!dropzone) return;
    //   const imagesCount = this.$refs.imageDropzone.dropzone.files.length;
    //   if (imagesCount > 0) {
    //     dropzone.style.border = "1px dashed #e6e7f1";
    //     this.errMediaMsg = "";
    //   } else {
    //     dropzone.style.border = "1px dashed red";
    //     this.errMediaMsg = "Please upload files of the following type: Image";
    //   }
    // },
  }
};
</script>

<style lang="scss" scoped>
::v-deep .ql-editor {
  min-height: 10rem;
  resize: vertical;

  p {
    margin-bottom: 1rem;
  }
}

.text-error {
  color: #f83245;
}

::v-deep .dz-default {
}

::v-deep .dz-preview {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-bottom: 1px solid #eeeff8;
}

::v-deep .dz-preview:hover {
  background-color: #f4f5fd;
  border: 0px solid transparent;
  border-radius: 10px;
}

::v-deep .dz-message {
  margin-bottom: 10px;
}

::v-deep .dz-error-message {
  display: none;
}

::v-deep .dz-success-mark {
  display: none;
}

::v-deep .dz-error-mark {
  display: none;
}

::v-deep .dz-details {
  display: flex;
}

::v-deep .dz-size {
  display: none;
}

::v-deep .dz-image {
  display: none;
}

::v-deep .dz-filename {
  color: #7a7b97;
}

::v-deep .dz-filename::before {
  font-family: 'FontAwesome';
  content: '\f15b';
  margin: 0 10px;
  color: #3c44b1 !important;
  font-size: 18px;
}

::v-deep .dz-remove {
  visibility: hidden;
  position: relative;
}

::v-deep .dz-remove::after {
  font-family: 'FontAwesome';
  color: #f83245 !important;
  visibility: visible;
  margin-right: 10px;
  font-size: 18px;
  content: '\f00d';
}

::v-deep .dz-remove:hover::after {
  font-family: 'FontAwesome';
  color: #f3939d !important;
}

.progress-bar__video {
  background-color: #28a745;
}

.box-submission {
  border: 1px solid #d1d2db;
  border-radius: 0.65rem;
}

.box-media {
  border: 1px solid #d1d2db;
  border-radius: 10.4px;
}

::v-deep .container__uploader {
  &.is-processing,
  &.is-disabled {
    .dz-message {
      display: none !important;
    }

    .dz-remove {
      display: none !important;
    }
  }
}

.icon_remove-video {
  &.is-disabled {
    display: none !important;
  }
}
</style>
